import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  File,
  Loader2,
  Mail,
  Paperclip,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useEmailAttachmentUrl,
  usePatientEmailAttachments,
  usePatientEmailMessages,
  type PatientEmailAttachment,
  type PatientEmailMessage,
  type PatientEmailThread,
} from '@/hooks/usePatientEmailThreads';
import { formatEmailMailbox, formatPatientEmailPartyLine, patientEmailStatusLabel } from '@/lib/patientEmail';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { formatMessageDateTime } from '@/utils/messageSender';

export function EmailDeliveryPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const label = patientEmailStatusLabel(status, t);
  switch (status) {
    case 'delivered':
    case 'received':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-green-tint px-2 py-0.5 text-[11px] font-medium text-ds-green-ink">
          <CheckCircle2 className="h-3 w-3" />
          {label}
        </span>
      );
    case 'sent':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-0.5 text-[11px] font-medium text-ds-teal">
          <Clock className="h-3 w-3" />
          {label}
        </span>
      );
    case 'queued':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-amber-tint px-2 py-0.5 text-[11px] font-medium text-ds-amber-ink">
          <Loader2 className="h-3 w-3 animate-spin" />
          {label}
        </span>
      );
    case 'failed':
    case 'bounced':
    case 'complained':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-red-tint px-2 py-0.5 text-[11px] font-medium text-ds-red-ink">
          <AlertTriangle className="h-3 w-3" />
          {label}
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-ds-neutral-tint px-2 py-0.5 text-[11px] text-ds-muted">
          {label}
        </span>
      );
  }
}

export function PatientEmailThreads({
  threads,
  highlightThreadId,
  clinicName,
  onReply,
}: {
  threads: PatientEmailThread[];
  highlightThreadId?: string | null;
  clinicName?: string | null;
  onReply?: (threadId: string) => void;
}) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(highlightThreadId ?? threads[0]?.id ?? null);

  useEffect(() => {
    if (highlightThreadId) setOpenId(highlightThreadId);
  }, [highlightThreadId]);

  if (threads.length === 0) return null;

  return (
    <div className="mt-s4 space-y-s2">
      <div className={cn(TYPE.label, 'text-ds-text')}>
        {t('patients.communication.email_threads', 'E-mailsamtaler')}
      </div>
      <ul className="divide-y divide-ds-line rounded-xl bg-ds-sunk">
        {threads.map((thread) => {
          const open = openId === thread.id;
          return (
            <li
              key={thread.id}
              id={`patient-email-thread-${thread.id}`}
              className={cn(highlightThreadId === thread.id && 'ring-2 ring-ds-teal')}
            >
              <button
                type="button"
                onClick={() => setOpenId(open ? null : thread.id)}
                className="flex w-full items-center gap-s2 px-s3 py-s3 text-left"
              >
                {open ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-ds-muted" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-ds-muted" />
                )}
                <Mail className="h-4 w-4 shrink-0 text-ds-muted" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ds-text">
                  {thread.subject || t('email.no_subject', 'Uden emne')}
                </span>
              </button>
              {open ? (
                <ThreadMessages thread={thread} clinicName={clinicName} onReply={onReply} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function EmailThreadTranscript({
  thread,
  clinicName,
}: {
  thread: PatientEmailThread;
  clinicName?: string | null;
}) {
  return <ThreadMessages thread={thread} clinicName={clinicName} />;
}

function ThreadMessages({
  thread,
  clinicName,
  onReply,
}: {
  thread: PatientEmailThread;
  clinicName?: string | null;
  onReply?: (threadId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: messages = [], isLoading } = usePatientEmailMessages(thread.id);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-s4 pb-s3 text-xs text-ds-muted">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('email.loading', 'Indlæser…')}
      </div>
    );
  }

  return (
    <div className="space-y-s3 px-s4 pb-s4">
      {messages.map((message) => (
        <ThreadMessageRow
          key={message.id}
          message={message}
          language={i18n.language}
          clinicName={clinicName}
        />
      ))}
      {onReply ? (
        <Button type="button" size="sm" variant="secondary" onClick={() => onReply(thread.id)}>
          {t('patients.communication.reply_email', 'Svar')}
        </Button>
      ) : null}
    </div>
  );
}

function ThreadMessageRow({
  message,
  language,
  clinicName,
}: {
  message: PatientEmailMessage;
  language: string;
  clinicName?: string | null;
}) {
  const { t } = useTranslation();
  const inbound = message.direction === 'inbound';
  const fromDisplay = inbound
    ? message.from_address
    : formatEmailMailbox(message.from_address, clinicName);
  const partyLine = formatPatientEmailPartyLine({
    from: fromDisplay,
    to: message.to_address,
    fromLabel: t('email.sent_from', 'Fra'),
    toLabel: t('email.sent_to', 'Til'),
  });
  return (
    <div className={cn('rounded-lg p-s3', inbound ? 'bg-ds-card' : 'bg-ds-teal-tint/40')}>
      <div className="mb-s1 flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-ds-muted">
          {inbound
            ? t('patients.communication.received', 'Modtaget')
            : t('patients.communication.sent', 'Sendt')}
        </span>
        <EmailDeliveryPill status={message.status} />
        {partyLine ? (
          <span className="text-[11px] text-ds-placeholder">{partyLine}</span>
        ) : null}
        <span className="ml-auto text-[11px] text-ds-muted">
          {formatMessageDateTime(message.created_at, language)}
        </span>
      </div>
      {message.subject ? (
        <div className="mb-s1 text-sm font-medium text-ds-text">{message.subject}</div>
      ) : null}
      <div className="whitespace-pre-wrap break-words text-sm text-ds-text-2">
        {message.text_body || t('email.empty_body', 'Ingen tekst')}
      </div>
      {message.error_message ? (
        <div className="mt-s2 rounded bg-ds-red-tint p-2 text-xs text-ds-red-ink ring-1 ring-ds-red-line">
          {t('email.error', 'Fejl')}: {message.error_message}
        </div>
      ) : null}
      <MessageAttachmentList messageId={message.id} />
    </div>
  );
}

function MessageAttachmentList({ messageId }: { messageId: string }) {
  const { data: files = [] } = usePatientEmailAttachments(messageId);
  if (files.length === 0) return null;
  return (
    <ul className="mt-s2 space-y-s1">
      {files.map((file) => (
        <li key={file.id}>
          <AttachmentLink file={file} />
        </li>
      ))}
    </ul>
  );
}

function AttachmentLink({ file }: { file: PatientEmailAttachment }) {
  const { data: url } = useEmailAttachmentUrl(file.storage_path);
  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs text-ds-teal hover:text-ds-teal-700"
    >
      {url ? <Paperclip className="h-3 w-3" /> : <File className="h-3 w-3" />}
      {file.filename}
    </a>
  );
}
